import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { Auth } from '../models/auth';
import { IdpAuth } from '../models/idpAuth';
import { Location } from '../models/location';
import { Settings } from '../models/settings';
import { Tenant } from '../models/tenant';
import { Translation } from '../models/translation';
import { User } from '../models/user';

export class MainStateModel {

  auth: Auth | null = null;
  idpAuth: IdpAuth | null = null;

  user: User | null = null;

  tenant: Tenant | null = null;

  translations: Translation[] = [];

  location: Location | null = null;

  logo: string = '';

  title: string = '';

  cookiesAccepted: boolean = false;

  settings: Settings | null = null;

  routePrefix: string | null = '';

  language: string | null = null;
  selectedLanguage: string | null = null;
  defaultLanguage: string = 'da';
}

export class SetAuth {
  static readonly type: string = '[Main] SetAuth';
  constructor(public auth: Auth | null) { }
}

export class SetIdpAuth {
  static readonly type: string = '[Main] SetIdpAuth';
  constructor(public idpAuth: IdpAuth | null) { }
}

export class SetUser {
  static readonly type: string = '[Main] SetUser';
  constructor(public user: User) { }
}

export class SetTenant {
  static readonly type: string = '[Main] SetTenant';
  constructor(public tenant: Tenant) { }
}

export class SetLogo {
  static readonly type: string = '[Main] SetLogo';
  constructor(public logo: string) { }
}

export class SetTitle {
  static readonly type: string = '[Main] SetTitle';
  constructor(public title: string) { }
}

export class SetRoutePrefix {
  static readonly type: string = '[Main] SetRoutePrefix';
  constructor(public prefix: string | null) { }
}

export class SetTranslations {
  static readonly type: string = '[Main] SetTranslations';
  constructor(public translations: Translation[]) { }
}

export class ResetMainState {
  static readonly type: string = '[Main] ResetMainState';
}

export class SetLocation {
  static readonly type: string = '[Main] SetLocation';
  constructor(public location: Location) { }
}

export class AcceptCookies {
  static readonly type: string = '[Main] AcceptCookies';
  constructor(public acceptCookies: boolean) { }
}

export class SetSettings {
  static readonly type: string = '[Main] SetSettings';
  constructor(public settings: Settings) { }
}

export class SetSelectedLanguage {
  static readonly type: string = '[Main] SetSelectedLanguage';
  constructor(public selectedLanguage: string | null) { }
}

export class SetDefaultLanguage {
  static readonly type: string = '[Main] SetDefaultLanguage';
  constructor(public defaultLanguage: string) { }
}

export class SetLanguage {
  static readonly type: string = '[Main] SetLanguage';
  constructor(public language: string | null) { }
}

@State<MainStateModel>({
  name: 'portalmain',
  defaults: new MainStateModel(),
})
@Injectable()
export class MainState implements NgxsOnInit {

  constructor(

  ) { }

  @Action(SetAuth)
  setAuth(ctx: StateContext<MainStateModel>, action: SetAuth): void {
    ctx.patchState({ auth: action.auth });
  }

  @Action(SetIdpAuth)
  setIdpAuth(ctx: StateContext<MainStateModel>, action: SetIdpAuth): void {
    ctx.patchState({ idpAuth: action.idpAuth });
  }

  @Action(SetUser)
  setUser(ctx: StateContext<MainStateModel>, action: SetUser): void {
    ctx.patchState({ user: action.user });
  }

  @Action(SetTenant)
  setTenant(ctx: StateContext<MainStateModel>, action: SetTenant): void {
    ctx.patchState({ tenant: action.tenant });
  }

  @Action(SetLogo)
  setLogo(ctx: StateContext<MainStateModel>, action: SetLogo): void {
    ctx.patchState({ logo: action.logo });
  }

  @Action(SetTitle)
  setTitle(ctx: StateContext<MainStateModel>, action: SetTitle): void {
    ctx.patchState({ title: action.title });
  }

  @Action(SetRoutePrefix)
  setRoutePrefix(ctx: StateContext<MainStateModel>, action: SetRoutePrefix): void {
    ctx.patchState({ routePrefix: action.prefix });
  }

  @Action(SetLocation)
  setLocation(ctx: StateContext<MainStateModel>, action: SetLocation): void {
    ctx.patchState({ location: action.location });
  }

  @Action(SetTranslations)
  setTranslations(ctx: StateContext<MainStateModel>, action: SetTranslations): void {
    ctx.patchState({ translations: action.translations || [] });
  }

  @Action(AcceptCookies)
  acceptCookies(ctx: StateContext<MainStateModel>, action: AcceptCookies): void {
    ctx.patchState({ cookiesAccepted: action.acceptCookies || false });
  }

  @Action(SetSettings)
  setSettings(ctx: StateContext<MainStateModel>, action: SetSettings): void {
    ctx.patchState({ settings: action.settings || null });
  }

  @Action(SetSelectedLanguage)
  setSelectedLanguage(ctx: StateContext<MainStateModel>, action: SetSelectedLanguage): void {
    ctx.patchState({ selectedLanguage: action.selectedLanguage });
  }

  @Action(SetDefaultLanguage)
  setDefaultLanguage(ctx: StateContext<MainStateModel>, action: SetDefaultLanguage): void {
    ctx.patchState({ defaultLanguage: action.defaultLanguage });
  }

  @Action(SetLanguage)
  setLanguage(ctx: StateContext<MainStateModel>, action: SetLanguage): void {
    ctx.patchState({ language: action.language });
  }

  @Action(ResetMainState)
  resetMainState(ctx: StateContext<MainStateModel>, action: ResetMainState): void {
    const oldState = ctx.getState();
    const newState = new MainStateModel();
    newState.logo = oldState.logo;
    newState.title = oldState.title;
    newState.cookiesAccepted = oldState.cookiesAccepted;
    newState.translations = oldState.translations;
    newState.selectedLanguage = oldState.selectedLanguage;
    newState.language = oldState.language;
    newState.defaultLanguage = oldState.defaultLanguage;
    ctx.setState(newState);
  }

  @Selector()
  static auth(state: MainStateModel): Auth | null {
    return state.auth;
  }

  @Selector()
  static idpAuth(state: MainStateModel): IdpAuth | null {
    return state.idpAuth;
  }

  @Selector()
  static user(state: MainStateModel): User | null {
    return state.user;
  }

  @Selector()
  static logo(state: MainStateModel): string | null {
    return state.logo || 'assets/img/fo-logo.svg';
  }

  @Selector()
  static title(state: MainStateModel): string | null {
    return state.title || 'Forsyning Online';
  }

  @Selector()
  static routePrefix(state: MainStateModel): string {
    return state.routePrefix || '';
  }

  @Selector()
  static tenant(state: MainStateModel): Tenant | null {
    return state.tenant;
  }

  @Selector()
  static location(state: MainStateModel): Location | null {
    return state.location;
  }

  @Selector()
  static translations(state: MainStateModel): Translation[] {
    return state.translations || [];
  }

  @Selector()
  static cookiesAccepted(state: MainStateModel): boolean {
    return state.cookiesAccepted || false;
  }

  @Selector()
  static settings(state: MainStateModel): Settings | null {
    return state.settings;
  }

  @Selector()
  static selectedLanguage(state: MainStateModel): string | null {
    return state.selectedLanguage;
  }

  @Selector()
  static defaultLanguage(state: MainStateModel): string {
    return state.defaultLanguage;
  }

  @Selector()
  static language(state: MainStateModel): string | null {
    return state.language;
  }

  ngxsOnInit(): void {

  }
}
